import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { DetalleOT, CatalogCategory, CatalogSubcategory, CatalogDetail, EvidenciaImg, CustomGarage } from "../../types/ot.types";
import { FileUploaderPreview } from "../FileUploaderPreview";

interface Step6Props {
    detalleOt: any;
    selectedGarage?: CustomGarage;
    archivoCotizacion?: EvidenciaImg;
    detallesOT: DetalleOT[];
    categories: CatalogCategory[];
    subcategoriesCache: Record<string, CatalogSubcategory[]>;
    detailsCache: Record<string, CatalogDetail[]>;
    updateDetalle: (id: number, field: keyof DetalleOT, value: any) => void;
    evidenciasReparacion?: Record<number, EvidenciaImg[]>;
    isCompleted?: boolean;
    readOnly?: boolean;
    userRole?: string;
    rechazarOrdenAEjecutivo: (motivo: string, tipoRechazo: number) => Promise<void>;
    ordenId?: string;
}

export const Step6AprobacionAdmin: React.FC<Step6Props> = ({
    detalleOt, selectedGarage, archivoCotizacion,
    detallesOT, categories, subcategoriesCache, detailsCache,
    updateDetalle, evidenciasReparacion, isCompleted = false, readOnly = false, userRole = "",
    rechazarOrdenAEjecutivo, ordenId = ""
}) => {
    const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});
    const [previewData, setPreviewData] = useState<{ url: string; type: string; name: string; } | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectType, setRejectType] = useState<number>(1);
    const [isRejecting, setIsRejecting] = useState(false);

    const isAdmin = userRole === "SYSADMIN" || userRole === "Super Administrador" || userRole === "Super Admin" || userRole === "Administrador Contraloria";

    useEffect(() => {
        document.body.style.overflow = previewData || showRejectModal ? "hidden" : "auto";
        return () => { document.body.style.overflow = "auto"; };
    }, [previewData, showRejectModal]);

    useEffect(() => {
        detallesOT.forEach(detalle => {
            if (detalle.aprobacion_cliente === "rechazado" && detalle.aprobacion_admin !== "rechazado") {
                updateDetalle(detalle.id, "aprobacion_admin", "rechazado");
                updateDetalle(detalle.id, "motivo_rechazo_admin", detalle.motivo_rechazo_cliente || "Rechazado en validación del cliente.");
            }
        });
    }, []);

    const toggleCollapse = (id: number) => setCollapsedItems((prev) => ({ ...prev, [id]: !prev[id] }));

    const handleRechazarOT = async () => {
        if (!rejectReason.trim()) return;
        setIsRejecting(true);
        await rechazarOrdenAEjecutivo(rejectReason, rejectType);
        setIsRejecting(false);
        setShowRejectModal(false);
        setRejectReason("");
        setRejectType(1);
    };

    // CÁLCULO AJUSTADO CON ISR PARA EL MONTO AUTORIZADO
    const montoAutorizado = detallesOT
        .filter(d => d.aprobacion_cliente === "aprobado")
        .reduce((acc, d) => {
            const cant = parseFloat(d.cantidad.toString()) || 0;
            const prec = parseFloat(d.precio_unitario_sin_iva.toString()) || 0;
            const iva = (parseFloat(d.porcentaje_iva.toString()) || 0) / 100;
            const isr = (parseFloat(d.porcentaje_isr?.toString()) || 0) / 100;
            const sub = cant * prec;
            return acc + (sub + (sub * iva) - (sub * isr));
        }, 0);

    return (
        <div id="ot-step6-aprobacion" className="row text-start position-relative">
            <div className="col-12 mb-4">
                <div className="card shadow-sm border-info">
                    <div className="card-header bg-info bg-opacity-10 fw-bold text-dark py-2">
                        <i className="fas fa-clipboard-list text-info me-2"></i> Resumen de la Orden Autorizada
                    </div>
                    <div className="card-body py-3 small">
                        <div className="row text-start">
                            <div className="col-md-4 border-end">
                                <span className="text-muted d-block mb-1">Taller Asignado:</span>
                                <span className="fw-bold"><i className="fas fa-wrench me-1 text-secondary"></i> {selectedGarage?.nombre_completo || 'No asignado'}</span>
                            </div>
                            <div className="col-md-4 border-end">
                                <span className="text-muted d-block mb-1">Fechas:</span>
                                <div><i className="fas fa-calendar-check me-1 text-secondary"></i> <strong>Ingreso:</strong> {detalleOt?.fecha_recepcion_bien ? new Date(detalleOt.fecha_recepcion_bien).toLocaleString('es-MX') : 'N/A'}</div>
                                <div><i className="fas fa-flag-checkered me-1 text-secondary"></i> <strong>Estimada:</strong> {detalleOt?.fecha_estimada_entrega ? new Date(detalleOt.fecha_estimada_entrega).toLocaleString('es-MX') : 'N/A'}</div>
                            </div>
                            <div className="col-md-4">
                                <span className="text-muted d-block mb-1">Monto Autorizado (Coordinador):</span>
                                <div className="fw-bold text-success fs-6 mb-1">${montoAutorizado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                {archivoCotizacion?.url ? (
                                    <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 mt-1" onClick={() => setPreviewData({ url: archivoCotizacion.url!, type: "pdf", name: "Cotización" })}>
                                        <i className="fas fa-file-pdf me-1"></i> Ver Cotización PDF
                                    </button>
                                ) : <span className="text-muted fst-italic">Sin documento</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="border rounded px-4 py-3 text-muted small flex-grow-1"><i className="fas fa-info-circle me-2 text-primary"></i>Valide administrativamente los conceptos autorizados.</div>
                {isAdmin && !isCompleted && (
                    <button type="button" className="btn btn-outline-danger fw-bold shadow-sm" onClick={() => setShowRejectModal(true)}><i className="fas fa-undo-alt me-2"></i> Rechazar a Ejecutivo</button>
                )}
            </div>

            <div className="col-12">
                {detallesOT.map((detalle, idx) => {
                    const isCollapsed = collapsedItems[detalle.id];
                    const estadoActual = detalle.aprobacion_admin;
                    const isRechazadoCliente = detalle.aprobacion_cliente === "rechazado";

                    const catLabel = detalle.categoria_label || 'Sin Categoría';
                    const subLabel = detalle.subcategoria_label || 'Sin Subcategoría';
                    const detLabel = detalle.subcategoria_detalle_label || 'Sin Detalle';
                    const evidenciasConcepto = evidenciasReparacion?.[detalle.id] || [];

                    // CÁLCULO AJUSTADO CON ISR
                    const cantidad = parseFloat(detalle.cantidad.toString()) || 0;
                    const precio_sin_iva = parseFloat(detalle.precio_unitario_sin_iva.toString()) || 0;
                    const iva_rate = (parseFloat(detalle.porcentaje_iva.toString()) || 0) / 100;
                    const isr_rate = (parseFloat(detalle.porcentaje_isr?.toString()) || 0) / 100;

                    const subtotal = cantidad * precio_sin_iva;
                    const total_cotizado = subtotal + (subtotal * iva_rate) - (subtotal * isr_rate);

                    let borderClass = estadoActual === "aprobado" ? "border-success" : (estadoActual === "rechazado" ? "border-danger" : "border-secondary");

                    return (
                        <div key={detalle.id} className={`card shadow-none mb-3 border border-start border-4 ${borderClass}`}>
                            <div className="card-header py-3 d-flex justify-content-between align-items-center border-bottom-0">
                                <div>
                                    <span className="text-muted fw-bold me-2 small">#{idx + 1}</span>
                                    <span className="fw-bold">{catLabel}</span> <span className="mx-2 text-muted">&gt;</span> <span className="text-muted small">{subLabel}</span> <span className="mx-2 text-muted">&gt;</span> <span className="text-muted small">{detLabel}</span>
                                </div>
                                <div className="text-end d-flex align-items-center gap-3">
                                    <div className={`fw-bold fs-5 ${isRechazadoCliente ? 'text-danger text-decoration-line-through' : ''}`}>${total_cotizado.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    <button type="button" className="btn btn-sm btn-light border text-muted px-3" onClick={() => toggleCollapse(detalle.id)}>{isCollapsed ? "Expandir" : "Ocultar"}</button>
                                </div>
                            </div>
                            {!isCollapsed && (
                                <div className="card-body pt-0 pb-4 px-4 border-top">
                                    <div className="row mt-3">
                                        <div className="col-md-7 border-end pe-md-4">
                                            <h6 className="fw-bold mb-2 small text-uppercase">Descripción y Evidencias</h6>
                                            <p className="text-muted small mb-3">{detalle.observaciones || "Sin descripción detallada."}</p>
                                            {evidenciasConcepto.length > 0 ? <FileUploaderPreview label="" archivos={evidenciasConcepto} setArchivos={() => { }} disabled={true} /> : <span className="small text-muted">Sin evidencia.</span>}
                                        </div>
                                        <div className="col-md-5 ps-md-4 mt-4 mt-md-0 d-flex flex-column">
                                            <h6 className="fw-bold mb-2 small text-uppercase">Resolución Operativa</h6>
                                            {isRechazadoCliente ? (
                                                <div className="alert alert-danger p-2 small mb-0">
                                                    <i className="fas fa-times-circle me-1"></i> Rechazado en validación del cliente: <strong>{detalle.motivo_rechazo_cliente}</strong>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="d-flex gap-2 mb-3">
                                                        <button type="button" disabled={readOnly || isCompleted || (!isAdmin)} className={`btn btn-sm flex-fill ${estadoActual === "aprobado" ? "btn-success" : "btn-outline-secondary"}`} onClick={() => { updateDetalle(detalle.id, "aprobacion_admin", "aprobado"); updateDetalle(detalle.id, "motivo_rechazo_admin", ""); }}>Aprobar</button>
                                                        <button type="button" disabled={readOnly || isCompleted || (!isAdmin)} className={`btn btn-sm flex-fill ${estadoActual === "rechazado" ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => updateDetalle(detalle.id, "aprobacion_admin", "rechazado")}>Rechazar</button>
                                                    </div>
                                                    {estadoActual === "rechazado" && (
                                                        <textarea className="form-control form-control-sm border-danger" rows={2} placeholder="Motivo administrativo..." value={detalle.motivo_rechazo_admin || ""} onChange={(e) => updateDetalle(detalle.id, "motivo_rechazo_admin", e.target.value)} disabled={readOnly || isCompleted || (!isAdmin)} required />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {showRejectModal && createPortal(
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                    <div className="modal d-block fade show" tabIndex={-1} style={{ zIndex: 1055 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-danger shadow-lg">
                                <div className="modal-header bg-danger text-white">
                                    <h5 className="modal-title fw-bold">Rechazar Orden</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowRejectModal(false)}></button>
                                </div>
                                <div className="modal-body text-start">
                                    <div className="mb-4">
                                        <label className="form-label fw-bold text-primary mb-3"><i className="fas fa-route me-2"></i>¿A qué paso deseas devolver la orden?</label>

                                        <div className={`form-check p-3 border rounded mb-2 ${rejectType === 1 ? 'bg-light border-primary' : ''}`} onClick={() => !isRejecting && setRejectType(1)} style={{ cursor: 'pointer' }}>
                                            <input className="form-check-input ms-1" type="radio" name="rejectType" checked={rejectType === 1} onChange={() => setRejectType(1)} disabled={isRejecting} />
                                            <label className="form-check-label ms-2 d-block" style={{ cursor: 'pointer' }}>
                                                <span className="fw-bold text-dark">Diagnóstico y Costos (Paso 3)</span><br />
                                                <span className="small text-muted">El ejecutivo deberá corregir conceptos, precios o cantidades.</span>
                                            </label>
                                        </div>

                                        <div className={`form-check p-3 border rounded ${rejectType === 2 ? 'bg-light border-primary' : ''}`} onClick={() => !isRejecting && setRejectType(2)} style={{ cursor: 'pointer' }}>
                                            <input className="form-check-input ms-1" type="radio" name="rejectType" checked={rejectType === 2} onChange={() => setRejectType(2)} disabled={isRejecting} />
                                            <label className="form-check-label ms-2 d-block" style={{ cursor: 'pointer' }}>
                                                <span className="fw-bold text-dark">Solo Evidencias (Paso 4)</span><br />
                                                <span className="small text-muted">Los costos quedan bloqueados. El ejecutivo solo podrá subir fotografías faltantes.</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="form-label fw-bold text-dark small">Motivo de la corrección:</label>
                                        <textarea className="form-control" rows={3} placeholder="Especifique qué debe corregir el ejecutivo..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} disabled={isRejecting}></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowRejectModal(false)} disabled={isRejecting}>Cancelar</button>
                                    <button type="button" className="btn btn-danger fw-bold" onClick={handleRechazarOT} disabled={isRejecting || !rejectReason.trim()}>{isRejecting ? "Procesando..." : "Confirmar Rechazo"}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>, document.body
            )}

            {previewData && createPortal(
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050, backgroundColor: "rgba(0,0,0,0.8)" }} onClick={() => setPreviewData(null)}></div>
                    <div className="modal d-block fade show" tabIndex={-1} style={{ zIndex: 1055, overflowY: "hidden" }} onClick={() => setPreviewData(null)}>
                        <div className="modal-dialog modal-dialog-centered modal-xl" style={{ height: "90vh", maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content shadow-lg border-0 bg-transparent h-100 w-100">
                                <div className="modal-header bg-white py-2 rounded-top">
                                    <h6 className="modal-title fw-bold text-truncate">Vista Previa</h6>
                                    <button type="button" className="btn-close" onClick={() => setPreviewData(null)}></button>
                                </div>
                                <div className="modal-body p-0 bg-dark rounded-bottom d-flex justify-content-center align-items-center" style={{ height: "100%", position: "relative" }}>
                                    <a href={previewData.url} download={previewData.name || "evidencia"} className="btn btn-light position-absolute shadow-sm" style={{ bottom: "20px", right: "20px", zIndex: 100 }}><i className="fas fa-download me-2"></i> Descargar</a>
                                    {previewData.type.includes("pdf") ? <embed src={previewData.url} type="application/pdf" width="100%" height="100%" /> : <img src={previewData.url} alt="Preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />}
                                </div>
                            </div>
                        </div>
                    </div>
                </>, document.body
            )}
        </div>
    );
};